import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy, inject } from '@angular/core';
import {
  ActionIconDef, Grid as InavistaGrid, GridActionIconClickEvent, GridConfig, GridRowCommitEvent, GridRowEditStartEvent,
  GridRowProblem, GridToolbarActionClickEvent,
} from '@inavista/components/grid';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { CellEdit, DropdownEditor, NumberEditor, TextEditor } from 'projects/service/src/app/shared/utils/cell-editors';
import { returnOrderStatuses } from '../../../models/orders.model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ApiResponse, UserRoles } from 'projects/service/src/app/shared/models/app.model';
import { InventoryService } from '../../../services/inventory.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import { MatDialog } from '@angular/material/dialog';
import {
  ADD_ITEM_ACTION, buildReturnOrderItemsActionIcons, buildReturnOrderItemsGridConfig, DELETE_ITEM_ACTION, OPEN_FIELD,
  RETURN_ORDER_ITEMS_COLUMNS, RETURN_ORDER_ITEMS_EDIT_FIELDS, ReturnOrderItemsGridContext, ROW_KEY,
} from './return-order-items.grid.config';

// The old grid's content was (popup body height - 330) tall, with its toolbar and header above it.
// The inavista grid draws them inside its own box.
const GRID_CHROME_PX = 92;

@Component({
    selector: 'app-return-order-items-tab',
    templateUrl: './return-order-items-tab.component.html',
    styleUrls: ['./return-order-items-tab.component.scss'],
    // The grid reads this.items, not an endpoint.
    providers: [...provideLocalGridRows()],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReturnOrderItemsTabComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {


  onItemUpdate = new Subject<string>();
  private destroy$: Subject<null> = new Subject();

  @Input() status: any;
  @Input() items = [];
  @Input() category: any;
  @Input() reasonsDDL: any;
  @Input() warehouse: any;

  @ViewChild('returnOrderItemsGrid') returnOrderItemsGrid: InavistaGrid;
  @ViewChildren('cellEditor') cellEditorTemplates: QueryList<TemplateRef<unknown>>;

  gridHeight: number;
  gridConfig: GridConfig = null;
  actionIcons: ActionIconDef[] = [];
  canEdit: boolean = false;
  skipBeginEditEvent: boolean = false;
  showItemAdvancedSearchPopup: boolean = false;
  selectedRowId: string;
  itemsList: any;
  reasonDescription: any;
  validationApplied: boolean = false;
  reasonValidationFail: boolean = false;

  itemsObject: DropdownEditor;

  itemDescriptionObject: TextEditor;

  itemGroup: any;
  itemGroupObject: TextEditor;

  itemQuantityObject: NumberEditor;

  itemReasonsObject: DropdownEditor;

  // The Quantity column's max rule (the technician's on-hand count), set by getTechnicianOnHandCount as before.
  private quantityMax: [number, string] = null;
  // Each editable column's editor (CellEditorHost), and the editors open in the row being edited.
  readonly editFields = RETURN_ORDER_ITEMS_EDIT_FIELDS;
  readonly edits: Record<string, CellEdit> = {
    itemCode: this.initItemsDDL(),
    itemDescription: this.initItemDescription(),
    serviceItemGroup: this.initItemGroup(),
    quantity: this.initItemQuantity(),
    reasonCode: this.initItemReason(),
  };
  readonly openEditors = new Map<string, CellEdit>();
  // The row open in the grid (the old grid.isEdit) and the open row's hidden columns.
  isEditing = false;
  hiddenEditRow: any = null;
  hiddenEditFields: string[] = [];
  private readonly gridRows = inject(LocalRowsGridApi);
  // A stable key per item object: lineNumber changes when the rows are renumbered.
  private readonly rowKeys = new WeakMap<object, number>();
  private nextRowKey = 1;

  constructor(
    private commonService: CommonService,
    private inventoryService: InventoryService,
    private dialog: MatDialog,
  ) {

  }

  ngOnInit(): void {
    this.gridHeight = document.getElementById('return-order-popup-body').clientHeight - 330 + GRID_CHROME_PX;
    this.canEdit = this.commonService.roleClaims['Return_ItemsTabGrid_Toolbar_Add_Item']?.visible;
    this.warehouse = this.commonService.USER.role === UserRoles.fst ? this.commonService.USER.warehouse : this.warehouse;
    this.setItemsData();
    // this.getItemList();
    this.onItemUpdate.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (!value) {
        this.getItemList();
      } else {
        this.getItemList(value);
      }
    });
  }

  // The editor templates are drawn: the grid is configured with them.
  ngAfterViewInit(): void {
    setTimeout(() => {
      const templates = this.cellEditorTemplates.toArray();
      const ctx: ReturnOrderItemsGridContext = {
        editors: Object.fromEntries(this.editFields.map((field, i) => [field, templates[i]])),
        canAdd: !!this.commonService.roleClaims['Return_ItemsTabGrid_Toolbar_Add_Item']?.visible,
        canExport: !!this.commonService.roleClaims['AllGrid_Toolbar_Excel_Export']?.visible,
        addDisabledReason: () => this.status === returnOrderStatuses.open ? null : 'Items can be added while the return order is Open.',
        editLockReason: () => this.status !== 'Order Sent' && this.canEdit ? null : 'Items can\'t be changed on this return order.',
        deleteLockReason: () => this.status === returnOrderStatuses.open && !this.commonService.roleClaims['Return_ItemsTabGrid_Action_Delete_Item']?.disable
          ? null : 'Items can be deleted while the return order is Open.',
        validate: () => this.validateOpenRow(),
      };
      this.gridRows.columns.set(LocalRowsGridApi.schema(RETURN_ORDER_ITEMS_COLUMNS));
      this.gridConfig = buildReturnOrderItemsGridConfig(ctx);
      this.actionIcons = buildReturnOrderItemsActionIcons(ctx);
      this.refreshRows();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.category?.previousValue || changes.status?.previousValue) {
      this.resetLineNumbers();
      this.toggleButtonDisable();
    }
    // The lock changed: rows open only while the user may change them.
    if (changes.status && this.gridConfig) {
      this.gridConfig = { ...this.gridConfig, editMode: this.status !== 'Order Sent' && this.canEdit ? 'inline' : 'none' };
    }
  }

  private keyOf(item: object): number {
    let key = this.rowKeys.get(item);
    if (key === undefined) {
      key = this.nextRowKey++;
      this.rowKeys.set(item, key);
    }
    return key;
  }

  // The rows the grid shows: the items not deleted (the old Query isDeleted equal false), Reason as its text (the old
  // foreign-key column).
  refreshRows() {
    if (!this.gridConfig) return;
    const rows = (this.items ?? []).filter(item => item.isDeleted === false).map(item => ({
      ...item,
      [ROW_KEY]: this.keyOf(item),
      reasonCode: (this.reasonsDDL ?? []).find(r => r.value === item.reasonCode)?.text ?? item.reasonCode,
    }));
    this.gridRows.rows.set(rows);
    this.returnOrderItemsGrid?.refresh();
  }

  /** The page's own item a grid row stands for. */
  itemFor(row: any) {
    return (this.items ?? []).find(item => this.rowKeys.get(item) === row?.[ROW_KEY]) ?? row;
  }

  getItemList(searchString: string = null) {
    this.inventoryService.popupLoader.next(true);
    const category = this.category.toLowerCase();
    this.inventoryService.getItems(this.warehouse, category, searchString)
      .subscribe((res: ApiResponse) => {
        if (res && res.result && res.result.length) {
          const existingItems = [];
          (this.items as any).forEach(item => {
            if (item.itemCode !== this.itemsObject?.value) {
              existingItems.push(item.itemCode);
            }
          });
          this.itemsList = res.result.filter(item => {
            item.value = item.item;
            item.text = `${item.item} | ${item.itemDescription}`;
            if (existingItems.length) {
              return !existingItems.includes(item.value)
            } else {
              return true;
            }
          });
          const items = res.result;
          let isCore = existingItems.length && items.find(item => item.item === existingItems[0])?.core;
          if (existingItems.length) {
            this.itemsList = items.filter(item => {
              item.text = item.item + ' | ' + (item.description || item.itemDescription);
              return !existingItems.includes(item.item) && isCore === item.core;
            });
          } else {
            this.itemsList = items.map(item => {
              item.text = item.item + ' | ' + (item.description || item.itemDescription);
              return item;
            });
          }

          if (this.itemsObject) {
            this.itemsObject.dataSource = this.itemsList;
            this.itemsObject.hideSpinner();
          }
          this.inventoryService.popupLoader.next(false);
        } else {
          this.itemsList = [];
          if (this.itemsObject) {
            this.itemsObject.hideSpinner();
            this.itemsObject.dataSource = [];
          }
          this.inventoryService.popupLoader.next(false);
        }
      });
  }

  // The row opened: the old actionBegin('beginEdit'), the editors draw, then the old actionComplete('beginEdit').
  onRowEditStart(event: GridRowEditStartEvent) {
    const rowData = this.itemFor(event.row);
    if (!rowData) return;
    this.isEditing = true;
    if (rowData.itemCode) {
      this.getTechnicianOnHandCount({ value: rowData.itemCode });
      if (this.skipBeginEditEvent) {
        this.skipBeginEditEvent = false;
      } else {
        this.getItemList(rowData.itemCode);
      }
    }
    else {
      this.getItemList();
    }
    const shown = this.returnOrderItemsGrid?.visibleColumnIds() ?? this.editFields;
    this.hiddenEditFields = this.editFields.filter(f => !shown.includes(f));
    this.hiddenEditRow = this.hiddenEditFields.length ? rowData : null;
    // Leaving the row always saves it, as the Syncfusion grid did: the row counts as changed from the start.
    const marked = this.returnOrderItemsGrid?.setDraftValue(OPEN_FIELD, String(Date.now()));
    setTimeout(() => {
      if (!marked) this.returnOrderItemsGrid?.setDraftValue(OPEN_FIELD, String(Date.now()));
      if (this.reasonValidationFail) {
        this.itemReasonsObject?.focusIn();
        this.reasonValidationFail = false;
      }
    });
  }

  // The Syncfusion rules of the columns, on the open editors: Reason required; Quantity at most the on-hand count.
  private validateOpenRow(): GridRowProblem | null {
    const reason = this.openEditors.get('reasonCode')?.read();
    if (reason === null || reason === undefined || reason === '') {
      return { column: 'reasonCode', message: 'Reason: This field is required.' };
    }
    const quantity = this.openEditors.get('quantity')?.read();
    if (this.quantityMax && quantity !== null && quantity !== undefined && quantity !== '' && +quantity > +this.quantityMax[0]) {
      return { column: 'quantity', message: this.quantityMax[1] };
    }
    return null;
  }

  // Enter, the ✓ icon or a click on another row: every editor's value goes into the item, as the Syncfusion grid did,
  // then the old actionComplete('save').
  onRowCommit(commit: GridRowCommitEvent) {
    const rowData = this.itemFor(commit.row);
    if (!rowData) return;
    for (const [field, edit] of this.openEditors) rowData[field] = edit.read();
    this.isEditing = false;
    this.hiddenEditRow = null;
    this.toggleButtonDisable();
    rowData.itemGroup = this.itemGroup;
    rowData.reasonDescription = this.reasonDescription;

    const emptyRecordsList = (this.items as any).filter((item, index) => {
      if (!item.itemCode) {
        item.rowIndex = index;
        return true;
      }
    });
    if (emptyRecordsList?.length < 2) {
      this.addItem();
      // Then the first empty row opens for editing, as the old grid did; not over a row the user opened meanwhile.
      const empty = (this.items as any).find(item => !item.itemCode && !item.isDeleted);
      if (empty) setTimeout(() => { if (!this.isEditing) this.returnOrderItemsGrid?.startEdit(this.keyOf(empty)); });
    }
    this.refreshRows();
  }

  // Esc or ✕: the row stays as it was (Syncfusion's cancel).
  onRowRevert() {
    this.isEditing = false;
    this.hiddenEditRow = null;
  }

  addEmptyRowsToItemsData() {
    for (let i = 0; i < 2; i++) {
      this.items.push(this.setItemsRow({}, this.items.length));
    }
  }

  setItemsData() {
    this.items?.map((item, index) => {
      return this.setItemsRow(item, index);
    });
    if (this.status === returnOrderStatuses.open) {
      this.addEmptyRowsToItemsData();
    }
  }

  setItemsRow(item, index) {
    item.id = item.id || 0,
      item.lineNumber = index + 1;
    item.itemCode = item.itemCode || item.item || '';
    item.itemDescription = item.itemDescription || item.description || '';
    item.itemGroup = item.itemGroup || '';
    item.serviceItemGroup = item.serviceItemGroup || '';
    item.quantity = item.quantity || 1;
    item.reasonCode = item.reasonCode || '';/*(
      this.category === returnOrdersCategoryTypes.serviceable
        ? ReturnOrderReasons.overStock.value
        : this.category === returnOrdersCategoryTypes.defective
          ? ReturnOrderReasons.defective.value
          : ReturnOrderReasons.damaged.value
    );*/
    item.reasonDescription = item.reasonDescription || null;
    item.createdBy = item.createdBy || '';
    item.createdByName = item.createdByName || null;
    item.lastUpdatedBy = item.lastUpdatedBy || '';
    item.lastUpdatedByName = item.lastUpdatedByName || '';
    item.isDeleted = item.isDeleted || false;
    return item;
  }

  getData() {
    return {
      itemList: (this.items as any).filter(x => x.itemCode)
    }
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey !== DELETE_ITEM_ACTION) return;
    if (!(this.status === returnOrderStatuses.open && !this.commonService.roleClaims['Return_ItemsTabGrid_Action_Delete_Item']?.disable)) return;
    const rowData = this.itemFor(event.row);
    this.endEdit();
    if (rowData.id || rowData.itemCode) {
      this.commonService.showConfirmation('Are you sure? You want to delete this item?').then(result => {
        if (result.isConfirmed) {
          rowData.lineNumber = rowData.lineNumber + '';
          rowData.isDeleted = true;
          this.resetLineNumbers();
          this.toggleButtonDisable();
        }
      })
    } else {
      const index = (this.items as any).indexOf(rowData);
      (this.items as any).splice(index, 1);
      this.resetLineNumbers();
      this.toggleButtonDisable();
    }
  }

  // The open row is saved, as the old endEdit (the Syncfusion grid's own save).
  private endEdit() {
    this.returnOrderItemsGrid?.saveBatch();
  }

  toggleButtonDisable() {
    const elem: any = document.getElementById('return-order-btn');
    elem.disabled = this.status !== returnOrderStatuses.open;
    if (!elem.disabled) {
      const index = this.findValidRow();
      elem.disabled = index < 0;
    }
  }

  resetLineNumbers() {
    (this.items as any).filter(item => item.isDeleted === false).map((item, index) => {
      item.lineNumber = index + 1;
      return item;
    });
    if ((this.items as any).filter(item => !item.itemCode).length < 2) {
      if ((this.items as any).filter(item => !item.itemCode).length === 0) {
        this.items.push(this.setItemsRow({}, this.items.length));
      }
      this.items.push(this.setItemsRow({}, this.items.length));
    }
    this.refreshRows();
  }

    showMasterItemCodePopup(eqData: any) {
        if (!eqData?.itemCode) return;
        const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
        panelClass: 'master-itemcode-popup-dialog',
        width: '80%',
        height: '80%',
        data: eqData
        });
        dialogRef.afterClosed().subscribe((result) => { });

    }

  // Add Item: refused while a row is open or empty rows are there, as before.
  onToolbarActionClick(event: GridToolbarActionClickEvent) {
    if (event.key !== ADD_ITEM_ACTION || this.status !== returnOrderStatuses.open) return;
    if (this.isEditing) {
      this.commonService.showNotification('warning', 'Item is in edit mode!');
      return;
    }
    this.inventoryService.popupLoader.next(true);
    if (this.items[this.items.length - 1]?.itemCode == '' || this.items[this.items.length - 2]?.itemCode == '') {
      this.commonService.showNotification('warning', 'There are already empty row(s) present');
      this.inventoryService.popupLoader.next(false);
    } else {
      this.addItem();
    }
  }

  // The old Excel export: the rows the grid shows, the visible columns, "Return Orders Items(<date>).xlsx".
  onExportRequested() {
    if (this.isEditing) {
      this.commonService.showNotification('warning', 'Item is in edit mode!');
      return;
    }
    exportInavistaGrid(this.returnOrderItemsGrid, 'Return Orders Items');
  }

  addItem() {
    let newIndex = (this.items as any).filter(item => !item.isDeleted).length;
    let newRow = this.setItemsRow({}, newIndex);
    (this.items as any).push(newRow);
    this.refreshRows();
    this.inventoryService.popupLoader.next(false);
  }

  validate() {
    this.endEdit();
    const index = this.findInvalidRow();
    if (!this.isEditing && index === -1) {
      return true;
    } else {
      return this.validationFailed();
    }
  }

  findValidRow() {
    return (this.items as any).findIndex(element => (element.reasonCode && element.quantity && element.itemCode) && !element.isDeleted);
  }

  findInvalidRow() {
    return (this.items as any).findIndex(element => (!element.reasonCode || !element.quantity) && element.itemCode && !element.isDeleted);
  }

  // The first invalid row opens for editing, as the old grid did, with the message saying what is missing.
  validationFailed() {
    this.commonService.showNotification('error', 'Mandatory fields in items are missing!');
    this.reasonValidationFail = true;
    const index = this.findInvalidRow();
    const invalid = index >= 0 ? this.items[index] : null;
    if (invalid) {
      const key = this.keyOf(invalid);
      this.returnOrderItemsGrid?.selectRow(key);
      // Opens on the missing field, only where the user may edit; read-only, the row stays highlighted.
      const column = invalid.quantity ? 'reasonCode' : 'quantity';
      setTimeout(() => this.returnOrderItemsGrid?.startEdit(key, { column }));
    }
    document.getElementById('items-tab-button').click();
    return false;
  }

  removeItems() {
    this.items = [];
    this.refreshRows();
  }

  showItemPopup() {
    this.showItemAdvancedSearchPopup = true;
  }

  onItemAdvancedSearchPopupClose(data: any) {
    setTimeout(() => {
      this.showItemAdvancedSearchPopup = false;
    }, 100);
    // Fixed (Martin, 2026-10-28): closing the search without picking an item threw here (data.item of nothing); the
    // open row now simply keeps its item.
    if (!data || !this.itemsObject) return;
    this.itemsObject.dataSource = [{
      text: `${data.item} | ${data.itemDescription}`,
      value: data.item,
      ...data
    }];
    this.itemsObject.value = data.item;
  }

  initItemsDDL(): CellEdit {
    return {
      read: () => this.itemsObject?.value,
      destroy: () => this.itemsObject?.destroy(),
      open: args => {
        this.selectedRowId = args.rowData?.rowId;
        this.itemsObject = new DropdownEditor({
          dataSource: this.itemsList,
          fields: { value: 'item', text: 'text' },
          value: args.rowData.itemCode,
          allowFiltering: true,
          showClearButton: true,
          popupWidth: '350px',
          openOnFocus: true,
          filtering: args => { this.onItemUpdate.next(args.text); },
          action: { title: 'Advanced Search', iconClass: 'fas fa-info', click: () => this.showItemPopup() },
          change: (event: any) => {
            if (!event.value) { return; }
            this.itemGroupObject.value = event.itemData.serviceItemGroup;
            this.itemDescriptionObject.value = event.itemData.description || event.itemData.itemDescription;
            this.itemGroup = event.itemData.itemGroup;
            if (event.value) {
              this.getTechnicianOnHandCount(event);
            }

            if (event.isInteracted) {
              this.itemQuantityObject.value = 1;
            }
          }
        });
        // The Advanced Search button is always shown on this grid.
        this.itemsObject.actionVisible.set(true);
        return this.itemsObject;
      }
    }
  }

  getTechnicianOnHandCount(args) {
    this.inventoryService.popupLoader.next(true);
    this.inventoryService.getTechnicianOnHandCount(args.value, this.warehouse, this.category)
      .subscribe((res: ApiResponse) => {
        if (res && res.result) {
          this.quantityMax = [res.result.onHandCount, 'On Hand: ' + res.result.onHandCount];
          if (this.itemQuantityObject) this.itemQuantityObject.max = res.result.onHandCount;
        }
        this.inventoryService.popupLoader.next(false);
      }, (error: HttpErrorResponse) => {
        this.inventoryService.popupLoader.next(false);
        throw error;
      });
  }

  initItemGroup(): CellEdit {
    return {
      read: () => this.itemGroupObject?.value,
      destroy: () => this.itemGroupObject?.destroy(),
      open: args => {
        return this.itemGroupObject = new TextEditor({
          value: args.rowData.serviceItemGroup,
          enabled: false
        });
      }
    }
  }

  initItemDescription(): CellEdit {
    return {
      read: () => this.itemDescriptionObject?.value,
      destroy: () => this.itemDescriptionObject?.destroy(),
      open: args => {
        return this.itemDescriptionObject = new TextEditor({
          value: args.rowData.itemDescription,
          enabled: false
        });
      }
    }
  }

  initItemQuantity(): CellEdit {
    return {
      read: () => this.itemQuantityObject?.value,
      destroy: () => this.itemQuantityObject?.destroy(),
      open: args => {
        return this.itemQuantityObject = new NumberEditor({
          value: args.rowData.quantity,
          min: 1,
          format: 'n',
          step: 1,
          decimals: 0,
        });
      }
    }
  }

  initItemReason(): CellEdit {
    return {
      read: () => this.itemReasonsObject?.value,
      destroy: () => this.itemReasonsObject?.destroy(),
      open: args => {
        return this.itemReasonsObject = new DropdownEditor({
          dataSource: this.reasonsDDL,
          fields: { value: 'value', text: 'text' },
          placeholder: 'Select Reason',
          value: args.rowData.reasonCode,
          allowFiltering: true,
          openOnFocus: true,
          change: (args: any) => {
            this.reasonDescription = args.itemData.description;
          }
        });
      }
    }
  }

  ngOnDestroy() {
    this.onItemUpdate.unsubscribe();
    this.destroy$.next(null);
    this.destroy$.unsubscribe();
  }
}
